import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ChevronRight, Calendar, Trash2, Search, X } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useAllPlanillas, useDeletePlanilla } from '../../hooks/usePlanillas';
import { useProjects } from '../../hooks/useProjects';
import { useAuthStore } from '../../stores/auth-store';

const ESTADO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'borrador', label: 'Borrador' },
  { value: 'pendiente_compras', label: 'Pend. Compras' },
  { value: 'pendiente_administrativo', label: 'Pend. Admin' },
  { value: 'correccion_solicitada', label: 'Corrección' },
  { value: 'revisada', label: 'Revisada' },
  { value: 'aprobada', label: 'Aprobada' },
  { value: 'pagada', label: 'Pagada' },
];

export function PlanillaListPage() {
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role);
  const [estadoFilter, setEstadoFilter] = useState('');
  const [page, setPage] = useState(1);
  const [projectFilter, setProjectFilter] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { data: projectsData } = useProjects({ page: 1, limit: 500 });
  const allProjects = projectsData?.data ?? [];
  const suggestions = allProjects.filter((p) => {
    if (!projectSearch) return true;
    const q = projectSearch.toLowerCase();
    return p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q);
  }).slice(0, 8);

  const handleSelectProject = (p: typeof allProjects[0]) => {
    setProjectFilter(p.id);
    setProjectSearch(`${p.codigo} — ${p.nombre}`);
    setShowSuggestions(false);
    setPage(1);
  };

  const handleClearProject = () => {
    setProjectFilter('');
    setProjectSearch('');
    setShowSuggestions(false);
    setPage(1);
  };

  const navigateToPlanilla = useCallback((planillaId: string, projectId: string, estado: string) => {
    if (userRole === 'compras' && estado === 'pendiente_compras') {
      navigate(`/compras/planillas/${planillaId}/descuentos`);
    } else {
      navigate(`/proyectos/${projectId}/planillas/${planillaId}`);
    }
  }, [navigate, userRole]);

  const { data, isLoading, error } = useAllPlanillas({
    estado: estadoFilter || undefined,
    projectId: projectFilter || undefined,
    page,
    limit: 20,
  });

  const planillas = data?.data ?? [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <div className="space-y-4 fade-up">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 mb-2">Error al cargar planillas</p>
        <Button variant="secondary" onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-up">
      {/* Header */}
      <h1 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
        <FileText className="w-6 h-6" />
        Planillas
      </h1>

      {/* Project search — relative z-10 so the dropdown overlaps estado chips below */}
      <div className="relative z-10 w-full sm:w-80">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
        <input
          type="text"
          className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-9 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Buscar planillas por proyecto..."
          value={projectSearch}
          onChange={(e) => {
            setProjectSearch(e.target.value);
            setProjectFilter('');
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        />
        {projectFilter && (
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); handleClearProject(); }}
          >
            <X size={14} />
          </button>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((p) => (
              <button
                key={p.id}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex flex-col border-b border-gray-50 last:border-0"
                onMouseDown={(e) => { e.preventDefault(); handleSelectProject(p); }}
              >
                <span className="text-sm font-medium text-gray-800">{p.nombre}</span>
                <span className="text-xs text-gray-400">{p.codigo}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status filters */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {ESTADO_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-full font-medium transition ${
              estadoFilter === opt.value
                ? 'bg-brand-dark text-white'
                : 'bg-white/60 text-gray-600 hover:bg-white'
            }`}
            onClick={() => { setEstadoFilter(opt.value); setPage(1); }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      {planillas.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay planillas</p>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                  <th className="pb-2 font-medium">N°</th>
                  <th className="pb-2 font-medium">Proyecto</th>
                  <th className="pb-2 font-medium">Período</th>
                  <th className="pb-2 font-medium">Residente</th>
                  <th className="pb-2 font-medium text-right">Total bruto</th>
                  <th className="pb-2 font-medium">Estado</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {planillas.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-gray-100 hover:bg-white/50 cursor-pointer"
                    onClick={() => navigateToPlanilla(p.id, p.project_id, p.estado)}
                  >
                    <td className="py-3 font-medium">{p.numero}</td>
                    <td className="py-3 text-gray-700">{p.project_nombre ?? '—'}</td>
                    <td className="py-3 text-gray-500">{p.fecha_desde} — {p.fecha_hasta}</td>
                    <td className="py-3 text-gray-600">{p.created_by.nombre}</td>
                    <td className="py-3 text-right font-medium">
                      ${p.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3">
                      <StatusBadge entity="planilla" status={p.estado} />
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        {p.estado === 'borrador' && (
                          <DeletePlanillaButton planillaId={p.id} />
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {planillas.map((p) => (
              <div
                key={p.id}
                className="cursor-pointer"
                onClick={() => navigateToPlanilla(p.id, p.project_id, p.estado)}
              >
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800">N°{p.numero}</p>
                        <StatusBadge entity="planilla" status={p.estado} />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{p.project_nombre ?? ''}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>{p.fecha_desde} — {p.fecha_hasta}</span>
                      </div>
                      <p className="text-xs text-gray-400">{p.created_by.nombre}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-brand-dark">
                        ${p.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="text-sm text-gray-500">
                {page} / {pagination.totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DeletePlanillaButton({ planillaId }: { planillaId: string }) {
  const deleteMutation = useDeletePlanilla();
  return (
    <button
      className="text-gray-400 hover:text-red-500"
      title="Eliminar planilla"
      onClick={async (e) => {
        e.stopPropagation();
        if (window.confirm('¿Eliminar esta planilla? Esta acción no se puede deshacer.')) {
          await deleteMutation.mutateAsync(planillaId);
        }
      }}
    >
      <Trash2 size={14} />
    </button>
  );
}
